<div class="fullcontainer pr-3">
  <br />

  <div class='titleContainer'>
    <h2 class='header' style='color:#006598;'>Confirm what Sage accepted</h2>
    <div>
      <button class='btn btn-secondary' routerLink='/invoices/sage-export/batches'>
        <i class="fas fa-clipboard-check"></i> All Batches
      </button>&nbsp;
      <button class='btn btn-secondary' routerLink='/invoices/sage-export'>
        <i class="fas fa-file-export"></i> Build a Batch
      </button>
    </div>
  </div>
  <br />

  @if (this.isError) {
    <div class='alert alert-danger'>{{ this.errorMessage }}</div>
  }

  @if (this.summary !== '') {
    <div class='alert alert-success'>{{ this.summary }}</div>
  }

  @if (this.loading) {
    <div class='sheet'>
      <p class='m-0'><i class="fas fa-spinner fa-spin"></i> Loading batch&hellip;</p>
    </div>
  }

  @if (!this.loading && this.batch) {
    <div class='sheet'>
      <div class='titleContainer'>
        <h1 class='header'>{{ this.batch.batchRef }}</h1>
      </div>

      <div class='row'>
        <div class='col-md-3'>
          <label class='mb-0 text-muted'>Built</label>
          <p>{{ this.formatDateTime(this.batch.exportedAt) }}</p>
        </div>
        <div class='col-md-3'>
          <label class='mb-0 text-muted'>Built by</label>
          <p>{{ this.batch.builtBy ? this.batch.builtBy : '—' }}</p>
        </div>
        <div class='col-md-3'>
          <label class='mb-0 text-muted'>Invoiced up to</label>
          <p>{{ this.formatDate(this.batch.upTo) }}</p>
        </div>
        <div class='col-md-3'>
          <label class='mb-0 text-muted'>In the file</label>
          <!-- Documents and lines are different counts and both are labelled. One row per
               document; one CSV line per invoice line item. -->
          <p>
            {{ this.batch.totals.documents }} document(s),
            {{ this.batch.lines }} line(s)
          </p>
        </div>
      </div>

      <div class='row'>
        <div class='col-md-3'>
          <label class='mb-0 text-muted'>Invoices / credit notes</label>
          <p>{{ this.batch.totals.invoices }} / {{ this.batch.totals.creditNotes }}</p>
        </div>
        <div class='col-md-3'>
          <label class='mb-0 text-muted'>Already confirmed in Sage</label>
          <p>{{ this.batch.totals.confirmed }}</p>
        </div>
        <div class='col-md-3'>
          <label class='mb-0 text-muted'>Still outstanding</label>
          <p>{{ this.batch.totals.outstanding }}</p>
        </div>
        <div class='col-md-3'>
          <label class='mb-0 text-muted'>Net exported</label>
          <p>{{ this.formatMoney(this.batch.totals.exportedNet) }}</p>
        </div>
      </div>

      @if (this.batch.csvAvailable) {
        <button class='btn btn-secondary' (click)='this.downloadAgain()'>
          <i class="fas fa-download"></i> Download the file again
        </button>

        <!-- Raised only once part of the batch is in Sage. The file still contains the accepted
             documents, and Sage does not deduplicate, so importing it a second time posts them
             again. The remedy is a new batch, which by construction leaves out what Sage took. -->
        @if (this.isPartlyInSage) {
          <div class='alert alert-warning mt-3 mb-0'>
            <strong>{{ this.alreadyConfirmedCount }} document(s) in this file are already in
            Sage.</strong>
            This copy is the record of what was sent, not something to import again &mdash; it
            still contains those documents, and Sage would post them a second time. To send what
            is still outstanding, build a new batch: it leaves out everything Sage has already
            taken.
          </div>
        }
      } @else {
        <button class='btn btn-secondary' disabled>
          <i class="fas fa-download"></i> Download the file again
        </button>
        <p class='text-muted mt-2 mb-0'>
          <small>
            The file for this batch was not kept &mdash; it was built before files were retained.
            Confirm against the copy you saved at the time. Building a new batch would not give
            you this file back: it would be a second file carrying the same documents, and Sage
            does not deduplicate.
          </small>
        </p>
      }
    </div>

    <div class='sheet'>
      <div class='titleContainer'>
        <h1 class='header'>What did Sage take?</h1>
      </div>

      <p class='text-muted'>
        Tick the documents Sage accepted, using its error report. Match on the
        <strong>Reference</strong> column &mdash; that is the string in the file and on Sage's
        report. Anything left unticked stays outstanding and comes back in the next batch, which
        is what stops it being posted twice.
      </p>

      <button class='btn btn-sm btn-outline-dark' (click)='this.tickAll()'>Tick all</button>&nbsp;
      <button class='btn btn-sm btn-outline-dark' (click)='this.untickAll()'>Untick all</button>

      <p class='mt-3'>
        <strong>{{ this.acceptedCount }}</strong> will be marked as in Sage.
        <strong>{{ this.outstandingCount }}</strong> will stay outstanding and return in the next
        batch.
      </p>

      <table class='table'>
        <thead>
          <th></th>
          <th>Type</th>
          <th>Reference</th>
          <th>Doc #</th>
          <th>Account Ref</th>
          <th>Account</th>
          <th>Date</th>
          <th class='text-right'>Net (as exported)</th>
          <th>Status</th>
          <th>Note</th>
        </thead>
        <tbody>
          @for (doc of this.documents; track doc.attemptId) {
            <tr [class.table-warning]='doc.changedSinceExport && !doc.missing && !doc.deleted'
                [class.table-danger]='doc.missing || doc.deleted'
                [class.text-muted]='doc.confirmed'>
              <td>
                <input type='checkbox'
                  [checked]='this.isTicked(doc)'
                  [disabled]='doc.confirmed'
                  (change)='this.onTick(doc, $event.target.checked)'>
              </td>
              <td>{{ this.docTypeLabel(doc) }}</td>
              <!-- First after the type on purpose: this is the only string that appears both in
                   the file and on Sage's error report, so it is what the operator matches on. -->
              <td><strong>{{ doc.reference }}</strong></td>
              <td>{{ doc.docId }}</td>
              <td>{{ doc.accountRef }}</td>
              <td>{{ doc.accountName }}</td>
              <td>{{ doc.date }}</td>
              <td class='text-right'>
                <!-- Null is a legacy batch that recorded no figure. Rendering it as £0.00 would
                     state an amount nobody exported. -->
                {{ this.formatMoney(doc.exportedNet) }}
                @if (doc.changedSinceExport) {
                  <br /><small class='text-danger'>now {{ this.formatMoney(doc.currentNet) }}</small>
                }
              </td>
              <td>
                @if (doc.confirmed) {
                  <span class='badge badge-success'>Confirmed</span>
                  <br /><small>{{ this.formatDateTime(doc.confirmedAt) }}</small>
                } @else {
                  <span class='badge badge-warning'>Outstanding</span>
                }
              </td>
              <td>
                @if (doc.deleted) {
                  <small class='text-danger'>This document has been deleted since the file was
                  built.</small>
                } @else if (doc.missing) {
                  <small class='text-danger'>This document no longer exists.</small>
                } @else if (doc.changedSinceExport) {
                  <small class='text-danger'>Changed since the file was built. The file holds the
                  exported figure.</small>
                }
                @if (doc.outcome) {
                  <small class='d-block'>{{ doc.outcome }}</small>
                }
              </td>
            </tr>
          }
        </tbody>
      </table>

      @if (this.documents.length === 0) {
        <p class='text-muted'>This batch has no documents recorded against it.</p>
      }

      <div class='form-group'>
        <label for='outcome'>What Sage reported</label>
        <textarea id='outcome' name='outcome' class='form-control' rows='5'
          [maxlength]='this.outcomeLimit'
          [(ngModel)]='this.outcome'
          placeholder='Paste or type what Sage said - which rows it rejected and why.'></textarea>
        <!-- The backend slices at 2000. A silent truncation loses the tail of a rejection
             message, which is the part naming what went wrong. -->
        <small class='form-text text-muted'>
          {{ this.outcome.length }} / {{ this.outcomeLimit }} characters. Anything beyond
          {{ this.outcomeLimit }} is not stored.
        </small>
      </div>

      <button class='btn btn-success' (click)='this.onSubmit()' [disabled]='this.saving'>
        @if (this.saving) {
          <i class="fas fa-spinner fa-spin"></i> Recording&hellip;
        } @else {
          Record what Sage accepted
        }
      </button>&nbsp;
      <button class='btn btn-danger' routerLink='/invoices/sage-export/batches'>Cancel</button>
    </div>
  }
</div>
